import React from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Button,
  Tooltip,
  Snackbar,
  CardMedia
} from '@mui/material';
import { Link, Delete } from '@mui/icons-material';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import { copyToClipboard } from '../../utils/clipboard';

const BackgroundMusicSelector = ({
  title = "Background Music",
  options = [],
  selectedValue = "0",
  isLocked = false,
  audioUrl = "",
  isLoading = false,
  onSelectionChange,
  onLockChange = () => { },
  onRemove = () => { },
  showCopyUrlButton = true,
  showLockToggle = true,
  ...props
}) => {
  const [open, setOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');

  const handleCopyUrl = () => {
    if (audioUrl) {
      copyToClipboard(audioUrl);
      setSnackbarMessage('URL copied to clipboard!');
      setOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpen(false);
    setSnackbarMessage('');
  };

  // Helper function to find option by value
  const findOptionByValue = (options, value) => {
    return options.find(option => option.value === value) || null;
  };

  return (
    <Card>
      <CardContent>
        <Box display="grid" gap={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Autocomplete
              fullWidth
              options={options}
              getOptionLabel={(option) => option.label}
              value={findOptionByValue(options, String(selectedValue))}
              onChange={(_, newValue) => {
                if (onSelectionChange) {
                  onSelectionChange(newValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Background Music Option"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={`${option.prop}-${option.value}`}>
                  {option.label}
                </li>
              )}
              loading={isLoading}
              loadingText="Loading background music..."
              noOptionsText="No background music found"
            />
            {showLockToggle && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isLocked}
                    onChange={(e) => {
                      if (onLockChange) {
                        onLockChange(e.target.checked);
                      }
                    }}
                    name="is_bgm_locked"
                  />
                }
                label="Lock Background Music"
                sx={{ whiteSpace: 'nowrap', ml: 1 }}
              />
            )}
          </Box>

          {audioUrl ? (
            <CardMedia
              component="div"
              {...props}
            >
              <AudioPlayer
                src={audioUrl}
                customAdditionalControls={[]}
                customVolumeControls={[]}
                layout="horizontal"
                showJumpControls={false}
                showDownloadProgress={true}
                autoPlay={false}
                autoPlayAfterSrcChange={false}
              />
            </CardMedia>
          ) : (
            <CardMedia
              component="div"
              {...props}
              sx={{
                height: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#FFFCF5'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No background music selected
              </Typography>
            </CardMedia>
          )}

          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
      </CardContent>
      <CardActions>
        {audioUrl && (
          <>
            <Button size="small" color="error" startIcon={<Delete />} onClick={onRemove}>
              Remove
            </Button>
            {showCopyUrlButton && (
              <Tooltip title={audioUrl} placement="bottom">
                <Button size="small" color="primary" startIcon={<Link />} onClick={handleCopyUrl}>
                  Copy URL
                </Button>
              </Tooltip>
            )}
          </>
        )}
      </CardActions>
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Card>
  );
};

export default BackgroundMusicSelector; 